// TODO: Decide whether this list spans BOTH Licensing + RM programs or
// only Licensing. Default for now: include everything. The "Engagement"
// filter pill (All / Licensing / Coravu RM) lets the user defer that
// decision without a code change.

import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import Svg, { Path } from "react-native-svg";

import { Skeleton } from "../../../components/ui";
import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { listMyCompetitions } from "../../../lib/clientCompetitions";
import {
  listAllCandidatesForClient,
  type AllCandidatesRow,
} from "../../../lib/clientDashboard";
import { addToShortlist, removeFromShortlist } from "../../../lib/shortlist";
import type { ClientCompetitionListRow } from "../../../types/database";

const THEME = PANE_THEMES.Client;

const BLACK = THEME.pageBg;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const TILE_BG = "rgba(28,43,26,0.40)";
const RED_BG = "rgba(220,80,80,0.08)";
const RED_BORDER = "rgba(220,80,80,0.40)";
const RED_TEXT = "rgba(255,180,180,0.92)";

const STORAGE_KEY = "coravu.overview.candidates.v1";

const PAGE_MAX_W = 1240;

type EngagementFilter = "all" | "licensing" | "rm";
type SortMode = "recent" | "semester" | "geography" | "submissions";

const TERM_ORDER: Record<string, number> = { Spring: 0, Summer: 1, Fall: 2, Winter: 3 };

type StoredFilters = {
  shortlistedOnly: boolean;
  engagement: EngagementFilter;
  programIds: string[];   // empty = "all"
  sortMode: SortMode;
};

const DEFAULT_FILTERS: StoredFilters = {
  shortlistedOnly: false,
  engagement: "all",
  programIds: [],
  sortMode: "recent",
};

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function semesterLabel(row: AllCandidatesRow): string | null {
  const t = row.graduation_term?.trim();
  const y = row.graduation_year;
  if (!t && !y) return null;
  if (t && y) return `${t} ${y}`;
  return t ?? String(y);
}

function geographyLabel(row: AllCandidatesRow): string | null {
  const c = row.city?.trim();
  const s = row.state?.trim();
  if (c && s) return `${c}, ${s}`;
  return c || s || null;
}

function readStored(): StoredFilters {
  if (Platform.OS !== "web") return DEFAULT_FILTERS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<StoredFilters>;
    return {
      shortlistedOnly: Boolean(parsed.shortlistedOnly),
      engagement:
        parsed.engagement === "licensing" || parsed.engagement === "rm"
          ? parsed.engagement
          : "all",
      programIds: Array.isArray(parsed.programIds)
        ? parsed.programIds.filter((x) => typeof x === "string")
        : [],
      sortMode:
        parsed.sortMode === "semester" ||
        parsed.sortMode === "geography" ||
        parsed.sortMode === "submissions"
          ? parsed.sortMode
          : "recent",
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

function writeStored(s: StoredFilters) {
  if (Platform.OS !== "web") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* quota or private mode — silent */
  }
}

function isWithinPastWeek(iso: string | null): boolean {
  if (!iso) return false;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return false;
  return ts >= Date.now() - 7 * 24 * 60 * 60 * 1000;
}

/* ─────────────────────────  Page  ───────────────────────── */

export default function ClientAllCandidatesScreen() {
  const [rows, setRows] = useState<AllCandidatesRow[]>([]);
  const [cases, setCases] = useState<ClientCompetitionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [stored, setStored] = useState<StoredFilters>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  // Hydrate persisted filters once.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    setStored(readStored());
  }, []);

  // Persist on change (skips first hydration tick).
  const persistRef = useRef(false);
  useEffect(() => {
    if (!persistRef.current) {
      persistRef.current = true;
      return;
    }
    writeStored(stored);
  }, [stored]);

  // Debounce search 200ms.
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, cands] = await Promise.all([
        listMyCompetitions(),
        listAllCandidatesForClient(),
      ]);
      setCases(list);
      setRows(cands);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load candidates.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  /* ─── Stat strip — derived from the FULL unfiltered set. ─── */
  const stats = useMemo(() => {
    let best = 0;
    let subs = 0;
    let active = 0;
    for (const r of rows) {
      if (r.shortlisted) best += 1;
      subs += r.submission_count;
      if (isWithinPastWeek(r.last_active_at)) active += 1;
    }
    return { best, total: rows.length, subs, active };
  }, [rows]);

  /* ─── Filter pipeline ─── */
  const filtered = useMemo(() => {
    const q = searchDebounced.trim().toLowerCase();
    const programSet = stored.programIds.length > 0 ? new Set(stored.programIds) : null;
    const list: AllCandidatesRow[] = [];
    for (const r of rows) {
      if (stored.shortlistedOnly && !r.shortlisted) continue;
      if (stored.engagement !== "all") {
        if (!r.cases.some((c) => c.engagement_model === stored.engagement)) continue;
      }
      if (programSet) {
        if (!r.cases.some((c) => programSet.has(c.competition_id))) continue;
      }
      if (q) {
        const hay = [
          r.full_name ?? "",
          r.major ?? "",
          r.university ?? "",
          r.city ?? "",
          r.state ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) continue;
      }
      list.push(r);
    }
    return list;
  }, [rows, stored.shortlistedOnly, stored.engagement, stored.programIds, searchDebounced]);

  /* ─── Sort + grouping ─── */
  type Group = { key: string; label: string; rows: AllCandidatesRow[] };

  const groups: Group[] = useMemo(() => {
    const list = [...filtered];
    if (stored.sortMode === "recent") {
      list.sort((a, b) => {
        const at = a.last_active_at ? new Date(a.last_active_at).getTime() : 0;
        const bt = b.last_active_at ? new Date(b.last_active_at).getTime() : 0;
        if (at !== bt) return bt - at;
        const ar = a.most_recent_registered_at ? new Date(a.most_recent_registered_at).getTime() : 0;
        const br = b.most_recent_registered_at ? new Date(b.most_recent_registered_at).getTime() : 0;
        return br - ar;
      });
      return [{ key: "all", label: "", rows: list }];
    }
    if (stored.sortMode === "submissions") {
      list.sort((a, b) => {
        if (b.submission_count !== a.submission_count) return b.submission_count - a.submission_count;
        const at = a.last_active_at ? new Date(a.last_active_at).getTime() : 0;
        const bt = b.last_active_at ? new Date(b.last_active_at).getTime() : 0;
        return bt - at;
      });
      return [{ key: "all", label: "", rows: list }];
    }
    if (stored.sortMode === "semester") {
      const buckets = new Map<string, AllCandidatesRow[]>();
      for (const r of list) {
        const lab = semesterLabel(r) ?? "No semester";
        if (!buckets.has(lab)) buckets.set(lab, []);
        buckets.get(lab)!.push(r);
      }
      const sortedKeys = [...buckets.keys()].sort((a, b) => {
        if (a === "No semester") return 1;
        if (b === "No semester") return -1;
        const [ta, ya] = a.split(" ");
        const [tb, yb] = b.split(" ");
        const ya_n = parseInt(ya, 10);
        const yb_n = parseInt(yb, 10);
        if (ya_n !== yb_n) return ya_n - yb_n;
        return (TERM_ORDER[ta] ?? 9) - (TERM_ORDER[tb] ?? 9);
      });
      return sortedKeys.map((k) => {
        const rs = buckets.get(k)!;
        rs.sort((a, b) => (a.full_name ?? "").localeCompare(b.full_name ?? ""));
        return { key: k, label: k, rows: rs };
      });
    }
    // Geography
    const buckets = new Map<string, AllCandidatesRow[]>();
    for (const r of list) {
      const k = r.state?.trim() ? r.state.trim() : "No location";
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k)!.push(r);
    }
    const sortedKeys = [...buckets.keys()].sort((a, b) => {
      if (a === "No location") return 1;
      if (b === "No location") return -1;
      return a.localeCompare(b);
    });
    return sortedKeys.map((k) => {
      const rs = buckets.get(k)!;
      rs.sort((a, b) => {
        const ca = (a.city ?? "").localeCompare(b.city ?? "");
        if (ca !== 0) return ca;
        return (a.full_name ?? "").localeCompare(b.full_name ?? "");
      });
      return { key: k, label: k, rows: rs };
    });
  }, [filtered, stored.sortMode]);

  const programCount = useMemo(
    () => new Set(rows.flatMap((r) => r.cases.map((c) => c.competition_id))).size,
    [rows]
  );

  const filtersAreDefault =
    !stored.shortlistedOnly &&
    stored.engagement === "all" &&
    stored.programIds.length === 0 &&
    !searchDebounced.trim();

  function clearFilters() {
    setSearchInput("");
    setSearchDebounced("");
    setStored((s) => ({ ...s, shortlistedOnly: false, engagement: "all", programIds: [] }));
  }

  async function handleToggleShortlist(row: AllCandidatesRow) {
    try {
      if (row.shortlisted) {
        const entry = row.cases.find((c) => c.shortlist_entry_id);
        if (!entry?.shortlist_entry_id) return;
        await removeFromShortlist(entry.shortlist_entry_id);
      } else {
        const primary = row.cases[0];
        if (!primary) return;
        await addToShortlist({ competitionId: primary.competition_id, studentId: row.student_id });
      }
      await load();
    } catch (e) {
      console.warn("[Candidates] shortlist toggle failed:", e);
    }
  }

  function handleViewProfile(row: AllCandidatesRow) {
    router.push(`/client/student/${row.student_id}` as Href);
  }

  /* ─── Render ─── */

  const isTrulyEmpty = !loading && !error && rows.length === 0;

  if (isTrulyEmpty) {
    return <TrulyEmptyHero />;
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }}>
      <View style={{ paddingHorizontal: 32, paddingVertical: 28, maxWidth: PAGE_MAX_W }}>
          {/* Hero */}
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
            <Text
              style={{
                fontFamily: FONTS.displaySemiBold,
                fontSize: 10,
                letterSpacing: 3,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Overview
            </Text>
          </View>
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.displayLight,
              fontSize: 30,
              lineHeight: 36,
              color: LINEN,
            }}
          >
            Candidates
          </Text>
          <Text
            style={{
              marginTop: 4,
              fontFamily: FONTS.bodyLight,
              fontSize: 13,
              color: WM,
            }}
          >
            {plural(rows.length, "candidate", "candidates")} across {plural(programCount, "program", "programs")}
          </Text>

          {/* Stat strip */}
          <View style={{ marginTop: 22, flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
            <StatTile
              label="Best candidates"
              value={String(stats.best)}
              valueColor={SAGE}
              onPress={() => setStored((s) => ({ ...s, shortlistedOnly: true }))}
            />
            <StatTile label="Total candidates" value={String(stats.total)} valueColor={LINEN} />
            <StatTile
              label="Submissions"
              value={String(stats.subs)}
              valueColor={AMBER}
              onPress={() => setStored((s) => ({ ...s, sortMode: "submissions" }))}
            />
            <StatTile label="Active this week" value={String(stats.active)} valueColor={SAGE} />
          </View>

          {/* Error banner */}
          {error ? (
            <View
              style={{
                marginTop: 18,
                paddingHorizontal: 14,
                paddingVertical: 10,
                borderWidth: 1,
                borderColor: RED_BORDER,
                backgroundColor: RED_BG,
                borderRadius: 4,
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED_TEXT }}>
                Couldn't load candidates: {error}
              </Text>
            </View>
          ) : null}

          {/* Filter bar (sticky on web) */}
          <View
            style={[
              {
                marginTop: 22,
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                paddingVertical: 8,
                backgroundColor: BLACK,
                zIndex: 5,
              } as ViewStyle,
              Platform.OS === "web" ? ({ position: "sticky", top: 0 } as unknown as ViewStyle) : null,
            ]}
          >
            <TextInput
              value={searchInput}
              onChangeText={setSearchInput}
              placeholder="Search by name, school, major, or city…"
              placeholderTextColor="rgba(255,255,255,0.32)"
              style={
                {
                  flex: 1,
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  height: 40,
                  backgroundColor: BLACK,
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 4,
                  color: W,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
                } as unknown as TextStyle
              }
            />
            <FilterPill
              label="Shortlisted only"
              active={stored.shortlistedOnly}
              onPress={() => setStored((s) => ({ ...s, shortlistedOnly: !s.shortlistedOnly }))}
            />
            <EngagementPill
              value={stored.engagement}
              onChange={(v) => setStored((s) => ({ ...s, engagement: v }))}
            />
            <ProgramPill
              cases={cases}
              selected={stored.programIds}
              onChange={(ids) => setStored((s) => ({ ...s, programIds: ids }))}
            />
          </View>

          {/* Sort bar */}
          <View
            style={{
              marginTop: 8,
              flexDirection: "row",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            <SortChip
              label="Most recent"
              active={stored.sortMode === "recent"}
              onPress={() => setStored((s) => ({ ...s, sortMode: "recent" }))}
            />
            <SortChip
              label="By semester"
              active={stored.sortMode === "semester"}
              onPress={() => setStored((s) => ({ ...s, sortMode: "semester" }))}
            />
            <SortChip
              label="By geography"
              active={stored.sortMode === "geography"}
              onPress={() => setStored((s) => ({ ...s, sortMode: "geography" }))}
            />
            <SortChip
              label="By submissions"
              active={stored.sortMode === "submissions"}
              onPress={() => setStored((s) => ({ ...s, sortMode: "submissions" }))}
            />
          </View>

          {/* Results */}
          <View style={{ marginTop: 18 }}>
            {loading ? (
              <View style={{ gap: 8 }}>
                {[0, 1, 2, 3, 4, 5, 6, 7].map((k) => (
                  <SkeletonRow key={k} />
                ))}
              </View>
            ) : filtered.length === 0 ? (
              <NoMatches onClear={clearFilters} canClear={!filtersAreDefault} />
            ) : (
              <View style={{ borderRadius: 4, borderWidth: 1, borderColor: BORDER, overflow: "hidden" }}>
                {groups.map((g, gi) => (
                  <View key={g.key}>
                    {g.label ? <GroupDivider label={g.label} count={g.rows.length} first={gi === 0} /> : null}
                    {g.rows.map((row, idx) => (
                      <CandidateRow
                        key={row.student_id}
                        row={row}
                        first={idx === 0 && !g.label}
                        onPress={() => handleViewProfile(row)}
                        onToggleShortlist={() => handleToggleShortlist(row)}
                        onViewProfile={() => handleViewProfile(row)}
                      />
                    ))}
                  </View>
                ))}
              </View>
            )}
          </View>
      </View>
    </ScrollView>
  );
}

/* ─────────────────────────  Sub-components  ───────────────────────── */

function StatTile({
  label,
  value,
  valueColor,
  onPress,
}: {
  label: string;
  value: string;
  valueColor: string;
  onPress?: () => void;
}) {
  const [hover, setHover] = useState(false);
  const interactive = Boolean(onPress);
  const web = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      disabled={!interactive}
      {...(web && interactive
        ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
        : {})}
      style={[
        {
          flexBasis: ("calc(25% - 9px)" as unknown) as number,
          flexGrow: 1,
          minWidth: 180,
          height: 76,
          paddingHorizontal: 16,
          paddingVertical: 12,
          backgroundColor: TILE_BG,
          borderWidth: 1,
          borderColor: hover && interactive ? "rgba(181,135,58,0.30)" : BORDER,
          borderRadius: 4,
          justifyContent: "space-between",
        } as ViewStyle,
        web && interactive
          ? ({ cursor: "pointer", transition: "border-color 150ms" } as object)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 22,
          color: valueColor,
        }}
      >
        {value}
      </Text>
    </Pressable>
  );
}

function FilterPill({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      {...(web ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const) : {})}
      style={[
        {
          height: 36,
          paddingHorizontal: 12,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? "rgba(181,135,58,0.55)" : web && hover ? "rgba(181,135,58,0.30)" : BORDER,
          backgroundColor: active
            ? "rgba(181,135,58,0.12)"
            : web && hover
              ? "rgba(255,255,255,0.05)"
              : "rgba(255,255,255,0.03)",
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        } as ViewStyle,
        web ? ({ cursor: "pointer", transition: "all 150ms" } as object) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: active ? AMBER : WM,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function EngagementPill({
  value,
  onChange,
}: {
  value: EngagementFilter;
  onChange: (v: EngagementFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 200,
  });
  const wrapperRef = useRef<View | null>(null);

  function openMenu() {
    if (Platform.OS === "web" && wrapperRef.current) {
      const node = wrapperRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
      const rect = node.getBoundingClientRect?.();
      if (rect) {
        setAnchor({
          top: Math.round(rect.bottom + 6),
          left: Math.max(8, Math.round(rect.right - 200)),
          width: 200,
        });
      }
    }
    setOpen(true);
  }

  const label =
    value === "all" ? "Managed by" : value === "licensing" ? "Self-managed only" : "Coravu-managed only";

  return (
    <View ref={wrapperRef} collapsable={false}>
      <FilterPill label={label} active={value !== "all"} onPress={openMenu} />
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable onPress={() => setOpen(false)} style={popoverBackdrop} />
        <View style={[popoverPanel(anchor)]}>
          <PopoverItem label="All" active={value === "all"} onPress={() => { onChange("all"); setOpen(false); }} />
          <PopoverItem label="Self-managed only" active={value === "licensing"} onPress={() => { onChange("licensing"); setOpen(false); }} />
          <PopoverItem label="Coravu-managed only" active={value === "rm"} onPress={() => { onChange("rm"); setOpen(false); }} />
        </View>
      </Modal>
    </View>
  );
}

function ProgramPill({
  cases,
  selected,
  onChange,
}: {
  cases: ClientCompetitionListRow[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 280,
  });
  const wrapperRef = useRef<View | null>(null);

  function openMenu() {
    if (Platform.OS === "web" && wrapperRef.current) {
      const node = wrapperRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
      const rect = node.getBoundingClientRect?.();
      if (rect) {
        setAnchor({
          top: Math.round(rect.bottom + 6),
          left: Math.max(8, Math.round(rect.right - 280)),
          width: 280,
        });
      }
    }
    setOpen(true);
  }

  const label =
    selected.length === 0 ? "Program" : `Program (${selected.length} of ${cases.length})`;

  function toggle(id: string) {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  }

  return (
    <View ref={wrapperRef} collapsable={false}>
      <FilterPill label={label} active={selected.length > 0} onPress={openMenu} />
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable onPress={() => setOpen(false)} style={popoverBackdrop} />
        <View style={[popoverPanel(anchor), { maxHeight: 360 }]}>
          <PopoverItem
            label="All programs"
            active={selected.length === 0}
            onPress={() => { onChange([]); }}
          />
          <View style={{ height: 1, backgroundColor: BORDER }} />
          {cases.length === 0 ? (
            <Text
              style={{
                paddingHorizontal: 14,
                paddingVertical: 12,
                fontFamily: FONTS.body,
                fontSize: 12,
                color: WM,
              }}
            >
              No programs yet.
            </Text>
          ) : (
            cases.map((c) => (
              <PopoverCheckItem
                key={c.id}
                label={c.name}
                checked={selected.includes(c.id)}
                onPress={() => toggle(c.id)}
              />
            ))
          )}
        </View>
      </Modal>
    </View>
  );
}

const popoverBackdrop: ViewStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
};

function popoverPanel(anchor: { top: number; left: number; width: number }): ViewStyle {
  return {
    position: "absolute",
    top: anchor.top,
    left: anchor.left,
    width: anchor.width,
    backgroundColor: BLACK,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 4,
    overflow: "hidden",
    ...(Platform.OS === "web"
      ? ({ boxShadow: "0 24px 48px -12px rgba(0,0,0,0.55)" } as unknown as ViewStyle)
      : {}),
  };
}

function PopoverItem({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      {...(web ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const) : {})}
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 10,
          backgroundColor: active
            ? "rgba(181,135,58,0.10)"
            : web && hover
              ? "rgba(255,255,255,0.04)"
              : "transparent",
          borderLeftWidth: 2,
          borderLeftColor: active ? AMBER : "transparent",
        } as ViewStyle,
        web ? ({ cursor: "pointer" } as object) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 12.5,
          color: active ? LINEN : WS,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function PopoverCheckItem({
  label,
  checked,
  onPress,
}: {
  label: string;
  checked: boolean;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      {...(web ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const) : {})}
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 10,
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          backgroundColor: web && hover ? "rgba(255,255,255,0.04)" : "transparent",
        } as ViewStyle,
        web ? ({ cursor: "pointer" } as object) : null,
      ]}
    >
      <View
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          borderWidth: 1,
          borderColor: checked ? AMBER : WL,
          backgroundColor: checked ? AMBER : "transparent",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {checked ? (
          <Svg width={10} height={10} viewBox="0 0 12 12" fill="none">
            <Path d="M2.5 6.5l2.5 2.5 4.5-5" stroke={FOREST} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </Svg>
        ) : null}
      </View>
      <Text
        style={{ flex: 1, fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: WS }}
        numberOfLines={1}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function SortChip({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      {...(web ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const) : {})}
      style={[
        {
          paddingHorizontal: 11,
          paddingVertical: 6,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? AMBER : web && hover ? "rgba(181,135,58,0.30)" : "transparent",
          backgroundColor: active ? "rgba(181,135,58,0.12)" : "transparent",
        } as ViewStyle,
        web ? ({ cursor: "pointer", transition: "all 120ms" } as object) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: active ? AMBER : WM,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function GroupDivider({ label, count, first }: { label: string; count: number; first: boolean }) {
  return (
    <View
      style={{
        paddingHorizontal: 18,
        paddingTop: first ? 14 : 22,
        paddingBottom: 6,
        borderTopWidth: first ? 0 : 1,
        borderTopColor: BORDER,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "baseline", justifyContent: "space-between" }}>
        <Text
          style={{ fontFamily: FONTS.displayLight, fontSize: 14, color: LINEN }}
          numberOfLines={1}
        >
          {label}
        </Text>
        <Text style={{ fontFamily: FONTS.body, fontSize: 10, color: WM }}>
          {plural(count, "candidate", "candidates")}
        </Text>
      </View>
      <View style={{ height: 1, backgroundColor: BORDER, marginTop: 6 }} />
    </View>
  );
}

function CandidateRow({
  row,
  first,
  onPress,
  onToggleShortlist,
  onViewProfile,
}: {
  row: AllCandidatesRow;
  first: boolean;
  onPress: () => void;
  onToggleShortlist: () => Promise<void>;
  onViewProfile: () => void;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  const sem = semesterLabel(row);
  const geo = geographyLabel(row) ?? row.state ?? null;

  return (
    <Pressable
      onPress={onPress}
      {...(web ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const) : {})}
      style={[
        {
          height: 72,
          paddingHorizontal: 18,
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          borderTopWidth: first ? 0 : 1,
          borderTopColor: BORDER,
          backgroundColor: web && hover ? "rgba(181,135,58,0.04)" : "transparent",
        } as ViewStyle,
        web ? ({ cursor: "pointer", transition: "background-color 120ms" } as object) : null,
      ]}
    >
      <Avatar avatarUrl={row.avatar_url} initials={row.initials} />

      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{ fontFamily: FONTS.bodyMedium, fontSize: 14, color: LINEN }}
          numberOfLines={1}
        >
          {row.full_name ?? "Pending profile"}
        </Text>
        <Text
          style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }}
          numberOfLines={1}
        >
          {[row.major, row.university].filter(Boolean).join(" · ")}
        </Text>
      </View>

      {sem ? <Chip>{sem}</Chip> : null}
      {geo ? <Chip>{geo}</Chip> : null}

      <Text
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 16,
          color: row.submission_count > 0 ? LINEN : WL,
          minWidth: 18,
          textAlign: "right",
        }}
      >
        {row.submission_count}
      </Text>

      {row.shortlisted ? <ShortlistStar /> : <View style={{ width: 14 }} />}

      <ContactButton
        row={row}
        onToggleShortlist={onToggleShortlist}
        onViewProfile={onViewProfile}
      />
    </Pressable>
  );
}

function Avatar({ avatarUrl, initials }: { avatarUrl: string | null; initials: string }) {
  return (
    <View
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: FOREST_MID,
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.22)",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {avatarUrl ? (
        <Image source={{ uri: avatarUrl }} style={{ width: 36, height: 36 }} resizeMode="cover" />
      ) : (
        <Text style={{ fontFamily: FONTS.displaySemiBold, fontSize: 14, color: AMBER }}>
          {initials}
        </Text>
      )}
    </View>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <View
      style={{
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.04)",
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <Text style={{ fontFamily: FONTS.body, fontSize: 10, color: WM }} numberOfLines={1}>
        {children}
      </Text>
    </View>
  );
}

function ShortlistStar() {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path
        d="M7 1.5l1.6 3.4 3.7.4-2.7 2.6.7 3.7L7 9.9 3.7 11.6l.7-3.7L1.7 5.3l3.7-.4L7 1.5z"
        stroke={SAGE}
        fill={SAGE}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function ContactButton({
  row,
  onToggleShortlist,
  onViewProfile,
}: {
  row: AllCandidatesRow;
  onToggleShortlist: () => Promise<void>;
  onViewProfile: () => void;
}) {
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 220,
  });
  const wrapperRef = useRef<View | null>(null);
  const web = Platform.OS === "web";

  function openMenu(e?: { stopPropagation?: () => void }) {
    e?.stopPropagation?.();
    if (web && wrapperRef.current) {
      const node = wrapperRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
      const rect = node.getBoundingClientRect?.();
      if (rect) {
        // Anchor to the right of the row, opening down. Keep inside viewport.
        const desiredLeft = Math.round(rect.right - 220);
        const safeLeft = Math.max(8, Math.min(desiredLeft, window.innerWidth - 232));
        const desiredTop = Math.round(rect.bottom + 6);
        const safeTop = Math.min(desiredTop, window.innerHeight - 160);
        setAnchor({ top: safeTop, left: safeLeft, width: 220 });
      }
    }
    setOpen(true);
  }

  return (
    <View ref={wrapperRef} collapsable={false}>
      <Pressable
        onPress={(e) => {
          e?.stopPropagation?.();
          openMenu();
        }}
        {...(web ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const) : {})}
        style={[
          {
            height: 28,
            paddingHorizontal: 12,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: AMBER,
            backgroundColor: web && hover ? AMBER : "transparent",
            justifyContent: "center",
          } as ViewStyle,
          web ? ({ cursor: "pointer", transition: "all 120ms" } as object) : null,
        ]}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: web && hover ? FOREST : AMBER,
          }}
        >
          Contact
        </Text>
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable onPress={() => setOpen(false)} style={popoverBackdrop} />
        <View style={[popoverPanel(anchor)]}>
          <PopoverItem
            label={row.shortlisted ? "Remove from shortlist" : "Add to shortlist"}
            active={false}
            onPress={async () => {
              setOpen(false);
              await onToggleShortlist();
            }}
          />
          <PopoverItem
            label="View profile →"
            active={false}
            onPress={() => {
              setOpen(false);
              onViewProfile();
            }}
          />
        </View>
      </Modal>
    </View>
  );
}

function SkeletonRow() {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 18,
        paddingVertical: 14,
        backgroundColor: TILE_BG,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <Skeleton width={36} height={36} radius={18} />
      <View style={{ flex: 1 }}>
        <Skeleton width={"50%" as unknown as number} height={13} />
        <Skeleton width={"75%" as unknown as number} height={11} style={{ marginTop: 8 }} />
      </View>
      <Skeleton width={70} height={20} radius={999} />
      <Skeleton width={84} height={28} radius={999} />
    </View>
  );
}

function NoMatches({ onClear, canClear }: { onClear: () => void; canClear: boolean }) {
  return (
    <View
      style={{
        padding: 28,
        backgroundColor: TILE_BG,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: BORDER,
        alignItems: "center",
      }}
    >
      <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}>
        No candidates match these filters.
      </Text>
      {canClear ? (
        <Pressable
          onPress={onClear}
          style={[
            { marginTop: 10 },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Clear filters
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function TrulyEmptyHero() {
  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 32,
        paddingVertical: 80,
      }}
    >
      <View style={{ width: "100%", maxWidth: 520 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.displaySemiBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Candidates
          </Text>
        </View>
        <Text
          style={{
            marginTop: 12,
            fontFamily: FONTS.displayLight,
            fontSize: 30,
            lineHeight: 36,
            color: LINEN,
          }}
        >
          No candidates yet.
        </Text>
        <Text
          style={{
            marginTop: 12,
            fontFamily: FONTS.bodyLight,
            fontSize: 13,
            lineHeight: 21,
            color: WM,
          }}
        >
          When students register for one of your programs, they'll show up here.
        </Text>
      </View>
    </View>
  );
}
